import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, IndianRupee } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';

// EMI Calculator Constants
const MIN_LOAN_AMOUNT = 100000; // ₹1 Lakh
const MAX_LOAN_AMOUNT = 10000000; // ₹1 Crore
const LOAN_AMOUNT_STEP = 100000; // ₹1 Lakh steps
const MIN_INTEREST_RATE = 6; // 6% per annum
const MAX_INTEREST_RATE = 20; // 20% per annum
const INTEREST_RATE_STEP = 0.1; // 0.1% steps
const MIN_TENURE_MONTHS = 12; // 1 year
const MAX_TENURE_MONTHS = 360; // 30 years
const TENURE_STEP_MONTHS = 12; // 1 year steps

export const EMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [interestRate, setInterestRate] = useState(9.5);
  const [tenure, setTenure] = useState(12);
  const [emi, setEmi] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  const calculateEMI = useCallback(() => {
    const principal = loanAmount;
    const ratePerMonth = interestRate / 12 / 100;
    const tenureMonths = tenure;

    if (ratePerMonth === 0) {
      const calculatedEMI = principal / tenureMonths;
      setEmi(calculatedEMI);
      setTotalAmount(principal);
      setTotalInterest(0);
    } else {
      const calculatedEMI = principal * ratePerMonth * Math.pow(1 + ratePerMonth, tenureMonths) / 
                           (Math.pow(1 + ratePerMonth, tenureMonths) - 1);
      const calculatedTotalAmount = calculatedEMI * tenureMonths;
      const calculatedTotalInterest = calculatedTotalAmount - principal;

      setEmi(calculatedEMI);
      setTotalAmount(calculatedTotalAmount);
      setTotalInterest(calculatedTotalInterest);
    }
  }, [loanAmount, interestRate, tenure]);

  useEffect(() => {
    calculateEMI();
  }, [calculateEMI]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <section id="calculator" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center">
              <Calculator className="w-8 h-8 text-emerald-600" />
            </div>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            EMI <span className="text-emerald-600">Calculator</span>
          </h2>
          <p className="text-xl text-slate-600">
            Calculate your monthly installments and plan your finances better
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calculator Input Card */}
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Loan Details</CardTitle>
                <CardDescription>Adjust the sliders to calculate your EMI</CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Loan Amount */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold text-slate-700">Loan Amount</Label>
                    <span className="text-xl font-bold text-emerald-600">{formatCurrency(loanAmount)}</span>
                  </div>
                  <Slider
                    value={[loanAmount]}
                    onValueChange={(value) => setLoanAmount(value[0])}
                    min={MIN_LOAN_AMOUNT}
                    max={MAX_LOAN_AMOUNT}
                    step={LOAN_AMOUNT_STEP}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-slate-500">
                    <span>₹1L</span>
                    <span>₹1Cr</span>
                  </div>
                </div>

                {/* Interest Rate */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold text-slate-700">Interest Rate (per annum)</Label>
                    <span className="text-xl font-bold text-emerald-600">{interestRate.toFixed(1)}%</span>
                  </div>
                  <Slider
                    value={[interestRate]}
                    onValueChange={(value) => setInterestRate(value[0])}
                    min={MIN_INTEREST_RATE}
                    max={MAX_INTEREST_RATE}
                    step={INTEREST_RATE_STEP}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-slate-500">
                    <span>6%</span>
                    <span>20%</span>
                  </div>
                </div>

                {/* Loan Tenure */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold text-slate-700">Loan Tenure</Label>
                    <span className="text-xl font-bold text-emerald-600">{tenure} months ({(tenure/12).toFixed(1)} years)</span>
                  </div>
                  <Slider
                    value={[tenure]}
                    onValueChange={(value) => setTenure(value[0])}
                    min={MIN_TENURE_MONTHS}
                    max={MAX_TENURE_MONTHS}
                    step={TENURE_STEP_MONTHS}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-slate-500">
                    <span>1 Year</span>
                    <span>30 Years</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results Card */}
            <div className="space-y-6">
              {/* EMI Card */}
              <Card className="border-2 border-emerald-200 bg-emerald-50 shadow-lg">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-slate-600 mb-2 font-medium">Your Monthly EMI</p>
                    <div className="flex items-center justify-center text-5xl font-bold text-emerald-600 mb-1">
                      <IndianRupee className="w-10 h-10" />
                      <span>{Math.round(emi).toLocaleString('en-IN')}</span>
                    </div>
                    <p className="text-sm text-slate-500">per month</p>
                  </div>
                </CardContent>
              </Card>

              {/* Breakdown Cards */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-2 border-slate-200">
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-500 mb-1">Principal Amount</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(loanAmount)}</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-slate-200">
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-500 mb-1">Total Interest</p>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(totalInterest)}</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-2 border-slate-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-slate-500 mb-1">Total Amount Payable</p>
                  <p className="text-3xl font-bold text-slate-900">{formatCurrency(totalAmount)}</p>
                </CardContent>
              </Card>

              {/* Visual Breakdown */}
              <Card className="border-2 border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Payment Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-slate-600">Principal</span>
                        <span className="text-sm font-semibold">{((loanAmount / totalAmount) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3">
                        <div 
                          className="bg-emerald-600 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${(loanAmount / totalAmount) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-slate-600">Interest</span>
                        <span className="text-sm font-semibold">{((totalInterest / totalAmount) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3">
                        <div 
                          className="bg-orange-600 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${(totalInterest / totalAmount) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
